const COLORS = {
  navy: "#1b1f4b",
  accent: "#2f5bea",
  green: "#16a36a",
  red: "#d6334a",
  orange: "#f08c1a",
  warn: "#d98a06",
  teal: "#14a3b8",
  purple: "#8b4fd8",
  pink: "#e0457b",
  muted: "#646a8a",
  grid: "#e3e5ee",
  text: "#161a3a",
  tooltipBg: "#161a3a",
};

export const ENTERPRISE_CHART_COLORS = [
  "#2f5bea",
  "#f08c1a",
  "#16a36a",
  "#8b4fd8",
  "#e0457b",
  "#14a3b8",
  "#c9a400",
  "#7a8095",
];

const LEGACY_COLOR_MAP = {
  "#ff5722": "#d6334a",
  "#ef4444": "#d6334a",
  "#548e25": "#16a36a",
  "#22c55e": "#16a36a",
  "#00c179": "#14a3b8",
  "#14b8a6": "#14a3b8",
  "#f7b400": "#f08c1a",
  "#f59e0b": "#f08c1a",
  "#0288d1": "#2f5bea",
  "#0f69a7": "#2f5bea",
};

const darkTooltip = {
  backgroundColor: COLORS.tooltipBg,
  borderColor: COLORS.tooltipBg,
  borderRadius: 8,
  shadow: true,
  padding: 10,
  style: { color: "#ffffff", fontSize: "12px", fontWeight: "500" },
  useHTML: true,
  outside: true,
  hideDelay: 50,
  stickOnContact: true,
};

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const deepMerge = (base, override) => {
  if (!isPlainObject(base)) return override;
  if (!isPlainObject(override)) return override === undefined ? base : override;
  const result = { ...base };
  Object.keys(override).forEach((key) => {
    const baseVal = base[key];
    const overVal = override[key];
    if (isPlainObject(baseVal) && isPlainObject(overVal)) {
      result[key] = deepMerge(baseVal, overVal);
    } else if (overVal !== undefined) {
      result[key] = overVal;
    }
  });
  return result;
};

const remapColor = (color) => {
  if (!color || typeof color !== "string") return color;
  return LEGACY_COLOR_MAP[color.toLowerCase()] || LEGACY_COLOR_MAP[color] || color;
};

const remapSeriesColors = (series) => {
  if (!Array.isArray(series)) return series;
  return series.map((serie) => {
    if (!serie || typeof serie !== "object") return serie;
    const next = { ...serie };
    if (next.color) next.color = remapColor(next.color);
    if (Array.isArray(next.data)) {
      next.data = next.data.map((point) => {
        if (point && typeof point === "object" && !Array.isArray(point)) {
          return point.color
            ? { ...point, color: remapColor(point.color) }
            : point;
        }
        return point;
      });
    }
    return next;
  });
};

const axisDefaults = {
  labels: {
    style: { color: COLORS.muted, fontSize: "11px", fontWeight: "500" },
  },
  title: {
    style: { color: COLORS.muted, fontSize: "11px", fontWeight: "600" },
  },
  lineColor: COLORS.grid,
  tickColor: COLORS.grid,
  gridLineColor: COLORS.grid,
};

const hasClickHandler = (options) => {
  const plot = options?.plotOptions || {};
  const seriesClick = plot.series?.point?.events?.click;
  const columnClick = plot.column?.events?.click || plot.column?.point?.events?.click;
  const pieClick = plot.pie?.point?.events?.click;
  const barClick = plot.bar?.events?.click;
  return !!(seriesClick || columnClick || pieClick || barClick);
};

const forceVisualPolish = (options) => {
  const chartType = options?.chart?.type;
  const next = { ...options };
  const clickable = hasClickHandler(options);

  next.chart = {
    ...next.chart,
    backgroundColor: "transparent",
    style: {
      fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
      ...(next.chart?.style || {}),
    },
  };
  next.credits = { enabled: false };

  next.legend = {
    ...next.legend,
    itemStyle: {
      color: COLORS.text,
      fontSize: "12px",
      fontWeight: "600",
      ...(next.legend?.itemStyle || {}),
      color: COLORS.text,
    },
    symbolRadius: 4,
  };

  next.tooltip = {
    ...darkTooltip,
    ...next.tooltip,
    backgroundColor: darkTooltip.backgroundColor,
    borderColor: darkTooltip.borderColor,
    borderRadius: 8,
    outside: true,
    useHTML: true,
    hideDelay: 50,
    stickOnContact: true,
    style: {
      ...darkTooltip.style,
      ...(next.tooltip?.style || {}),
      color: "#fff",
    },
  };

  const plot = { ...(next.plotOptions || {}) };

  if (chartType === "pie" || plot.pie) {
    const pie = { ...(plot.pie || {}) };
    const inner = String(pie.innerSize || "");
    // Keep explicit ring cutouts (e.g. Bayanat 74%) intact
    if (!inner || inner === "50%" || inner === "40%") pie.innerSize = "58%";
    if (options?.chart?.height === 112 || options?.__opsRing) {
      pie.innerSize = pie.innerSize || "74%";
    }
    pie.borderWidth = 2;
    pie.borderColor = "#ffffff";
    pie.states = {
      ...(pie.states || {}),
      hover: { halo: { size: 8 }, brightness: 0.05, ...(pie.states?.hover || {}) },
    };
    if (clickable || pie.allowPointSelect || pie.point?.events?.click) {
      pie.cursor = "pointer";
    }
    pie.dataLabels = {
      ...(pie.dataLabels || {}),
      style: {
        color: COLORS.text,
        fontSize: "11px",
        fontWeight: "700",
        textOutline: "none",
        ...(pie.dataLabels?.style || {}),
      },
    };
    plot.pie = pie;
  }

  if (chartType === "column" || chartType === "bar" || plot.column || plot.bar) {
    plot.column = {
      ...(plot.column || {}),
      borderRadius: 5,
      borderWidth: 0,
      cursor: clickable || plot.column?.events?.click ? "pointer" : plot.column?.cursor,
    };
    plot.bar = {
      ...(plot.bar || {}),
      borderRadius: 5,
      borderWidth: 0,
      cursor: clickable || plot.bar?.events?.click ? "pointer" : plot.bar?.cursor,
    };
  }

  plot.series = {
    ...(plot.series || {}),
    animation: { duration: 400 },
    borderWidth: 0,
    cursor:
      clickable || plot.series?.point?.events?.click
        ? "pointer"
        : plot.series?.cursor,
  };

  next.plotOptions = plot;

  if (Array.isArray(next.colors)) {
    next.colors = next.colors.map(remapColor);
  } else {
    next.colors = ENTERPRISE_CHART_COLORS;
  }
  if (next.series) next.series = remapSeriesColors(next.series);

  return next;
};

export const applyEnterpriseChartTheme = (options = {}) => {
  if (!options || typeof options !== "object") return options;

  const defaults = {
    chart: { backgroundColor: "transparent", spacing: [8, 8, 8, 8] },
    colors: ENTERPRISE_CHART_COLORS,
    credits: { enabled: false },
    title: {
      style: { color: COLORS.text, fontSize: "13px", fontWeight: "700" },
    },
    xAxis: axisDefaults,
    yAxis: {
      ...axisDefaults,
      title: { text: null, style: axisDefaults.title.style },
    },
    legend: {
      itemStyle: { color: COLORS.text, fontSize: "12px", fontWeight: "600" },
    },
    tooltip: darkTooltip,
    plotOptions: {
      pie: { borderWidth: 2, borderColor: "#fff", innerSize: "58%" },
      column: { borderRadius: 5, borderWidth: 0 },
      bar: { borderRadius: 5, borderWidth: 0 },
    },
  };

  let themed = deepMerge(defaults, options);
  if (Array.isArray(options.xAxis)) {
    themed.xAxis = options.xAxis.map((axis) => deepMerge(axisDefaults, axis));
  }
  if (Array.isArray(options.yAxis)) {
    themed.yAxis = options.yAxis.map((axis) =>
      deepMerge(
        {
          ...axisDefaults,
          title: { text: null, style: axisDefaults.title.style },
        },
        axis,
      ),
    );
  }
  return forceVisualPolish(themed);
};

export const HC_CONTAINER_PROPS = {
  className: "exd-hc",
  style: { height: "100%", overflow: "visible", width: "100%" },
};

export { COLORS, darkTooltip };
